package it.neckar.lizergy.model.price

import it.neckar.editHistory.PositionEditHistory
import it.neckar.lizergy.model.company.CompanyCode
import it.neckar.lizergy.model.company.CompanyProfile
import it.neckar.lizergy.model.company.MainCompanyProfile
import it.neckar.lizergy.model.company.NeckarITCompanyProfile
import it.neckar.lizergy.model.financial.currency.Money
import it.neckar.lizergy.model.income.IncomePercentage
import it.neckar.lizergy.model.income.IncomePercentages
import it.neckar.lizergy.model.invoice.InvoiceNumber
import it.neckar.lizergy.model.price.EarningsDistribution.EarningsDistributionId
import it.neckar.open.provider.DoubleProvider.Companion.nowMillis
import it.neckar.open.unit.si.ms
import it.neckar.user.UserLoginName
import it.neckar.uuid.HasUuid
import it.neckar.uuid.NativeUuid
import it.neckar.uuid.randomUuid4
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.uuid.Uuid

interface EarningsDistribution : HasUuid {
  val id: EarningsDistributionId
  val tippgeber: EarningsEntry
  val vertriebProjekterfassung: EarningsEntry
  val vertriebAngebotsvorstellung: EarningsEntry
  val technischePlanungDach: EarningsEntry
  val technischePlanungElektrik: EarningsEntry
  val montageDach: EarningsEntry
  val montageGeruest: EarningsEntry
  val elektroInstallation: EarningsEntry
  val netzvoranfrage: EarningsEntry
  val neckarITAccountingStatus: EarningsEntry
  val selectedForAccounting: Boolean

  override val uuid: Uuid
    get() = id.uuid

  val all: List<EarningsEntry>

  val allIncludingSoftware: List<EarningsEntry>


  @Serializable
  data class EarningsDistributionId(val uuid: NativeUuid) {

    override fun toString(): String {
      return uuid.toString()
    }

    fun format(): String {
      return uuid.toString()
    }

    companion object {
      fun random(): EarningsDistributionId {
        return EarningsDistributionId(randomUuid4())
      }
    }
  }

}

@Serializable
enum class AccountingStatus {
  Pending,
  Accounted,
}

interface EarningsEntry {
  val company: CompanyCode
  val manualEarningsHistory: PositionEditHistory<Money?>
  val invoiceHistory: PositionEditHistory<InvoiceNumber?>
}


@Serializable
data class ResolvedEarningsDistribution(
  override val id: EarningsDistributionId,
  override val tippgeber: ResolvedCompanyEarningsEntry,
  override val vertriebProjekterfassung: ResolvedCompanyEarningsEntry,
  override val vertriebAngebotsvorstellung: ResolvedCompanyEarningsEntry,
  override val technischePlanungDach: ResolvedCompanyEarningsEntry,
  override val technischePlanungElektrik: ResolvedCompanyEarningsEntry,
  override val montageDach: ResolvedCompanyEarningsEntry,
  override val montageGeruest: ResolvedCompanyEarningsEntry,
  override val elektroInstallation: ResolvedCompanyEarningsEntry,
  override val netzvoranfrage: ResolvedCompanyEarningsEntry,
  override val neckarITAccountingStatus: ResolvedNeckarITEarningsEntry,
  override val selectedForAccounting: Boolean,
) : EarningsDistribution {

  override val all: List<ResolvedCompanyEarningsEntry>
    get() = listOf(
      tippgeber,
      vertriebProjekterfassung,
      vertriebAngebotsvorstellung,
      technischePlanungDach,
      technischePlanungElektrik,
      montageDach,
      montageGeruest,
      elektroInstallation,
      netzvoranfrage,
    )

  override val allIncludingSoftware: List<ResolvedEarningsEntry>
    get() = all + neckarITAccountingStatus

  val includesPartnerCompanies: Boolean
    get() = all.any { it.companyProfile !is MainCompanyProfile }

  val isPending: Boolean
    get() = allIncludingSoftware.any { it.accountingStatus == AccountingStatus.Pending }

  val isFullyAccounted: Boolean
    get() = allIncludingSoftware.all { it.accountingStatus == AccountingStatus.Accounted }

  val isFullyAccountedAt: @ms Double?
    get() = if (isFullyAccounted) allIncludingSoftware.maxOf { it.lastEditedTime } else null

  val isNeckarITAccounted: Boolean
    get() = neckarITAccountingStatus.accountingStatus == AccountingStatus.Accounted

  val neckarITWasAccountedAt: @ms Double?
    get() = if (neckarITAccountingStatus.accountingStatus == AccountingStatus.Accounted) neckarITAccountingStatus.lastEditedTime else null

  val contributingCompanies: List<CompanyCode>
    get() = all.filter { it.companyProfile !is MainCompanyProfile }.map { it.company }.distinct()

  fun duplicate(mapOfOldToNewUuids: MutableMap<Uuid, Uuid>): ResolvedEarningsDistribution {
    val newId = EarningsDistributionId.random()
    mapOfOldToNewUuids[id.uuid] = newId.uuid
    return copy(id = newId)
  }


  companion object {
    fun getEmpty(id: EarningsDistributionId = EarningsDistributionId.random(), defaultCompany: CompanyProfile, loggedInUser: UserLoginName, now: @ms Double = nowMillis()): ResolvedEarningsDistribution {
      return ResolvedEarningsDistribution(
        id = id,
        tippgeber = ResolvedCompanyEarningsEntry(
          companyProfile = defaultCompany,
          manualEarningsHistory = PositionEditHistory(null, loggedInUser, now),
          invoiceHistory = PositionEditHistory(null, loggedInUser, now),
          incomePercentage = IncomePercentages.tippgeber,
        ),
        vertriebProjekterfassung = ResolvedCompanyEarningsEntry(
          companyProfile = defaultCompany,
          manualEarningsHistory = PositionEditHistory(null, loggedInUser, now),
          invoiceHistory = PositionEditHistory(null, loggedInUser, now),
          incomePercentage = IncomePercentages.vertriebProjekterfassung,
        ),
        vertriebAngebotsvorstellung = ResolvedCompanyEarningsEntry(
          companyProfile = defaultCompany,
          manualEarningsHistory = PositionEditHistory(null, loggedInUser, now),
          invoiceHistory = PositionEditHistory(null, loggedInUser, now),
          incomePercentage = IncomePercentages.vertriebAngebotsvorstellung,
        ),
        technischePlanungDach = ResolvedCompanyEarningsEntry(
          companyProfile = defaultCompany,
          manualEarningsHistory = PositionEditHistory(null, loggedInUser, now),
          invoiceHistory = PositionEditHistory(null, loggedInUser, now),
          incomePercentage = IncomePercentages.technischePlanungDach,
        ),
        technischePlanungElektrik = ResolvedCompanyEarningsEntry(
          companyProfile = defaultCompany,
          manualEarningsHistory = PositionEditHistory(null, loggedInUser, now),
          invoiceHistory = PositionEditHistory(null, loggedInUser, now),
          incomePercentage = IncomePercentages.technischePlanungElektrik,
        ),
        montageDach = ResolvedCompanyEarningsEntry(
          companyProfile = defaultCompany,
          manualEarningsHistory = PositionEditHistory(null, loggedInUser, now),
          invoiceHistory = PositionEditHistory(null, loggedInUser, now),
          incomePercentage = IncomePercentages.montageDach,
        ),
        montageGeruest = ResolvedCompanyEarningsEntry(
          companyProfile = defaultCompany,
          manualEarningsHistory = PositionEditHistory(null, loggedInUser, now),
          invoiceHistory = PositionEditHistory(null, loggedInUser, now),
          incomePercentage = IncomePercentages.montageGeruest,
        ),
        elektroInstallation = ResolvedCompanyEarningsEntry(
          companyProfile = defaultCompany,
          manualEarningsHistory = PositionEditHistory(null, loggedInUser, now),
          invoiceHistory = PositionEditHistory(null, loggedInUser, now),
          incomePercentage = IncomePercentages.elektroInstallation,
        ),
        netzvoranfrage = ResolvedCompanyEarningsEntry(
          companyProfile = defaultCompany,
          manualEarningsHistory = PositionEditHistory(null, loggedInUser, now),
          invoiceHistory = PositionEditHistory(null, loggedInUser, now),
          incomePercentage = IncomePercentages.netzvoranfrage,
        ),
        neckarITAccountingStatus = ResolvedNeckarITEarningsEntry(
          manualEarningsHistory = PositionEditHistory(null, loggedInUser, now),
          invoiceHistory = PositionEditHistory(null, loggedInUser, now),
        ),
        selectedForAccounting = false,
      )
    }
  }
}

@Serializable
sealed interface ResolvedEarningsEntry : EarningsEntry {
  val companyProfile: CompanyProfile
  val manualEarnings: Money?

  val accountingStatus: AccountingStatus
  val lastEditedTime: @ms Double

  override val company: CompanyCode
    get() = companyProfile.companyCode
}

@Serializable
@SerialName("Company")
data class ResolvedCompanyEarningsEntry(
  override val companyProfile: CompanyProfile,
  override val manualEarningsHistory: PositionEditHistory<Money?>,
  override val invoiceHistory: PositionEditHistory<InvoiceNumber?>,
  val incomePercentage: IncomePercentage,
) : ResolvedEarningsEntry {
  override val manualEarnings: Money?
    get() = when (companyProfile) {
      is MainCompanyProfile -> null
      else -> manualEarningsHistory.currentValue
    }

  override val accountingStatus: AccountingStatus
    get() = when (companyProfile) {
      is MainCompanyProfile -> AccountingStatus.Accounted
      else -> if (invoiceHistory.currentValue?.value.isNullOrBlank()) AccountingStatus.Pending else AccountingStatus.Accounted
    }

  override val lastEditedTime: @ms Double
    get() = invoiceHistory.lastEditedTime
}

@Serializable
@SerialName("NeckarIT")
data class ResolvedNeckarITEarningsEntry(
  override val manualEarningsHistory: PositionEditHistory<Money?>,
  override val invoiceHistory: PositionEditHistory<InvoiceNumber?>,
) : ResolvedEarningsEntry {
  override val companyProfile: CompanyProfile
    get() = NeckarITCompanyProfile

  override val manualEarnings: Money?
    get() = manualEarningsHistory.currentValue

  override val accountingStatus: AccountingStatus
    get() = if (invoiceHistory.currentValue?.value.isNullOrBlank()) AccountingStatus.Pending else AccountingStatus.Accounted

  override val lastEditedTime: @ms Double
    get() = invoiceHistory.lastEditedTime
}
